import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Dynamic backend loader so we don't bundle 650+ KiB of raw translation JSON
// (both sv and en) into the initial critical entry bundle.
const dynamicBackend = {
  type: 'backend' as const,
  async read(language: string, _namespace: string, callback: (err: unknown, data?: unknown) => void) {
    try {
      const normalized = language.toLowerCase();
      if (normalized.startsWith('sv')) {
        const sv = await import('../translations/sv.json');
        callback(null, sv.default);
      } else {
        const en = await import('../translations/en.json');
        callback(null, en.default);
      }
    } catch (err) {
      callback(err, null);
    }
  },
};

i18n
  .use(LanguageDetector)
  .use(dynamicBackend)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // react already safes from xss
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    react: {
      useSuspense: true,
    },
  });

// The UI only ships sv and en — anything that isn't Swedish renders the
// English bundle (see dynamicBackend), so that is the language to declare.
export function uiLanguage(lng: string | undefined = i18n.language): 'sv' | 'en' {
  return lng?.toLowerCase().startsWith('sv') ? 'sv' : 'en';
}

// index.html ships lang="sv" for crawlers; keep <html lang> in step with the
// language actually rendered so screen readers pick the right voice.
// 'initialized' covers the detector's first pick, which fires before
// 'languageChanged' listeners registered after init() would see it.
function syncHtmlLang(lng?: string) {
  // Unit tests import this module in a plain node environment.
  if (typeof document === 'undefined') return;
  document.documentElement.lang = uiLanguage(lng);
}
i18n.on('languageChanged', syncHtmlLang);
i18n.on('initialized', () => syncHtmlLang());
if (i18n.isInitialized) syncHtmlLang();

export default i18n;

